import { i18n } from "../boot/i18n";
import { store } from "../store";
import DataStructure, { sanitize } from "./DataStructure";
import DeliveryStatus, { DELIVERY_STATUS_FIELDS } from "./DeliveryStatus";
import PickupStatus, { PICKUP_STATUS_FIELDS } from "./PickupStatus";
import ServiceStatus, { SERVICE_STATUS_FIELDS } from "./ServiceStatus";
import { SALE_LABOR_STATUS_TYPES } from "./SaleLaborStatus";
import SaleItem from "./SaleItem";
import DocumentRef from "./DocumentRef";
import Address from "./Address";
import { ShipTo } from "./BillTo";
import { dateToTimestamp, deepFreeze, timestampToDate } from "../util/misc";
import { LOCATION_INFO } from "../constants";
import COLORS from "../colors";
import {
  cloneDeep,
  compact,
  every,
  flatten,
  groupBy,
  isArray,
  isEmpty,
  isEqual,
  isObject,
  isString,
  last,
  pick,
  pickBy,
  sortBy,
  uniq,
} from "lodash";
import { endOfDay, format, isSameDay, startOfDay } from "date-fns";
import { formatDurationMs } from "../util/misc";
import { diff } from "deep-object-diff";

const instancesCache = new WeakMap();
const instancesAsEventCache = new WeakMap();
const groupedInstancesCache = new WeakMap();
const statusCache = new WeakMap();

// The `item`/`items` getters are read during rendering and lazily kick off a
// fetch when the referenced docs aren't in the store yet. Without a guard,
// every re-render (and each store SET the fetch itself commits) re-invokes the
// getter, which re-dispatches the fetch — a self-amplifying render/fetch storm
// that only settles once every doc has loaded. These in-flight sets dedupe the
// fetch per target so a getter read triggers at most one outstanding request.
// Keyed by string (frozen instances can't hold the flag themselves).
const itemFetchInFlight = new Set();
const itemsFetchInFlight = new Set();
const additionalSalesFetchInFlight = new Set();
const workOrderFetchInFlight = new Set();

export const clearEventStatusCache = (event) => {
  if (event) {
    statusCache.delete(event);
  }
};

export const eventSort = (a, b) => {
  return (
    a.start - b.start ||
    b.end - a.end ||
    (a.data.type === b.data.type ? 0 : a.data.type < b.data.type ? -1 : 1) ||
    a.data.created - b.data.created
  );
};

export const getInstanceRange = (instances) => {
  let start = null;
  let end = null;
  (isArray(instances) ? instances : Object.values(instances || {})).forEach(
    (i) => {
      if (!start || i.start < start) {
        start = i.start;
      }
      if (!end || i.end > end) {
        end = i.end;
      }
    }
  );
  return { start, end };
};

export const EVENT_FIELDS = {
  // CalendarEvent info
  title: "",
  type: "",
  location: null,
  locationDetail: "",
  saleLocation: null,
  stops: [],
  start: "",
  end: "",
  allDay: false,
  description: "",
  attachments: [],
  participants: [],
  everyone: false,
  instances: {},
  item: null,
  items: null,
  quantity: 0,
  additionalSales: {},
  status: {},
  vehicles: [],
  rescheduleReason: null,
  locked: false,
  pencil: false,

  // Meta info
  created: null,
  createdBy: null,
  modified: null,
  modifiedBy: null,
};
deepFreeze(EVENT_FIELDS);

export const INSTALLATION_STATUS_FIELDS = {
  isTemporary: false,
  shouldInvoiceAnyway: false,
};
deepFreeze(INSTALLATION_STATUS_FIELDS);

export const EVENT_TYPES = {
  personal: {
    label: "Personal",
    icon: "personal",
    color: "personal",
    hasVehicle: true,
  },
  ooo: {
    label: "Out of Office",
    icon: "ooo",
    color: "ooo",
    hasVehicle: true,
  },
  meeting: {
    label: "In-Office Meeting",
    icon: "meeting",
    color: "meeting",
    hasVehicle: false,
  },
  delivery: {
    label: "Delivery",
    icon: "delivery",
    color: "delivery",
    itemStore: "sales",
    itemsStore: "saleItems",
    hasVehicle: true,
    requiresVehicle: true,
    isFieldEvent: true,
  },
  pickup: {
    label: "Customer Pick-Up",
    icon: "pickup",
    color: "pickup",
    itemStore: "sales",
    itemsStore: "saleItems",
    hasVehicle: false,
    requiresVehicle: false,
    isFieldEvent: false,
    duration: 15,
  },
  installation: {
    label: "Installation",
    icon: "installation",
    color: "installation",
    itemStore: "sales",
    itemsStore: "saleLabors",
    hasVehicle: true,
    requiresVehicle: false,
    isFieldEvent: true,
  },
  service: {
    label: "Service",
    icon: "service",
    color: "service",
    itemStore: "serviceLeads",
    hasVehicle: true,
    requiresVehicle: true,
    isFieldEvent: true,
  },
};
deepFreeze(EVENT_TYPES);

export default class CalendarEvent extends DataStructure {
  constructor(id, data = {}, instanceID) {
    // Backward compatibility
    if (data.vehicle) {
      data = { ...data, vehicles: [data.vehicle] };
    }
    if (data.type === "installation" && !isArray(data.items)) {
      data.items = data.items ? Object.keys(data.items) : [];
    }

    data = sanitize(data, EVENT_FIELDS);

    // Convert to Date
    data.created = timestampToDate(data.created);
    data.modified = timestampToDate(data.modified);
    data.start = timestampToDate(data.start);
    data.end = timestampToDate(data.end);

    if (data.instances) {
      // The instances object (or individual instances) may be frozen store data
      // if CalendarEvent is built from a shallow copy of an existing event.
      if (Object.isFrozen(data.instances)) {
        data.instances = { ...data.instances };
      }
      Object.keys(data.instances).forEach((id) => {
        let instance = data.instances[id];
        if (Object.isFrozen(instance)) {
          data.instances[id] = {
            ...instance,
            start: timestampToDate(instance.start),
            end: timestampToDate(instance.end),
          };
        } else {
          instance.start = timestampToDate(instance.start);
          instance.end = timestampToDate(instance.end);
        }
      });
      if (instanceID && instanceID in data.instances) {
        data = { ...data, ...data.instances[instanceID] };
        delete data.instances;
      }
    }

    super(data);

    this.id = id || null;
    this.instanceID = instanceID || null;
    this.permissions = Object.freeze({
      view: CalendarEvent.canBeViewedBy,
      create: CalendarEvent.canBeCreatedBy,
      edit: this.canBeEditedBy,
      delete: this.canBeDeletedBy,
      unlock: this.canBeUnlockedBy,
    });

    // Documents
    // Keep attachments as raw data; the `attachments` getter wraps them in
    // DocumentRef instances lazily. Creating DocumentRef objects here added
    // allocation churn for every event created from the listener.
    this.data.attachments = this.data.attachments || [];

    // Status
    // Normalize status defaults and timestamp fields without constructing the
    // expensive status object (which computes derived type and triggers item
    // and location creation). The `status` getter remains lazy for consumers
    // that actually need the computed status.
    const STATUS_FIELDS_BY_TYPE = {
      delivery: DELIVERY_STATUS_FIELDS,
      installation: INSTALLATION_STATUS_FIELDS,
      pickup: PICKUP_STATUS_FIELDS,
      service: SERVICE_STATUS_FIELDS,
    };
    if (this.data.status && STATUS_FIELDS_BY_TYPE[this.data.type]) {
      this.data.status = sanitize(
        this.data.status,
        STATUS_FIELDS_BY_TYPE[this.data.type]
      );
      [
        "loadingStarted",
        "loadingCompleted",
        "departed",
        "arrived",
        "unloaded",
        "completed",
      ].forEach((key) => {
        if (key in this.data.status) {
          this.data.status[key] = timestampToDate(this.data.status[key]);
        }
      });
    }
  }

  // Permissions

  static canBeViewedBy(user) {
    return user.isEmployee;
  }

  static canBeCreatedBy(user) {
    return user.isEmployee;
  }

  canBeEditedBy(user) {
    const isPersonal = this.data.type === "personal";
    return (
      (!this.locked || this.canBeUnlockedBy(user)) &&
      (user.isAdmin ||
        (user.isEmployee &&
          (user.hasRole("scheduling") ||
            user.id === this.data.createdBy ||
            (!isPersonal && this.data.everyone) ||
            this.data.participants.includes(user.id))))
    );
  }

  canBeDeletedBy(user) {
    return (
      !this.locked &&
      (user.isAdmin ||
        user.hasRole("scheduling") ||
        user.id === this.data.createdBy)
    );
  }

  canBeUnlockedBy(user) {
    return user.isAdmin || (user.isEmployee && user.hasRole("scheduling"));
  }

  // Database

  get path() {
    return CalendarEvent.getPath(this.id);
  }

  static getPath(id) {
    let path = `events`;
    if (id) {
      path += `/${id}`;
    }
    return path;
  }

  static toFirestore(event) {
    if (
      event.data.status &&
      !event.isPencil &&
      ["delivery", "pickup", "service"].includes(event.data.type) &&
      !Object.isFrozen(event.data.status)
    ) {
      const status = event.status;
      if (status && typeof status.get === "function") {
        event.data.status.type = status.get();
      }
    }
    return event.data;
  }

  static fromFirestore(snapshot, options) {
    const data = snapshot.data(options);
    return new CalendarEvent(snapshot.id, data);
  }

  async getItem() {
    if (!this.data.item) {
      return null;
    }

    let item = this.data.item;
    switch (this.type.itemStore) {
      case "sales":
        // Dedupe concurrent fetches for the same sale: the `item` getter that
        // calls this runs on every render and would otherwise re-dispatch
        // before the first request resolves.
        if (itemFetchInFlight.has(item)) {
          return;
        }
        itemFetchInFlight.add(item);
        try {
          return await store.dispatch("GET_SALE", {
            projectID: item.slice(0, -1),
            saleID: item.slice(-1),
          });
        } finally {
          itemFetchInFlight.delete(item);
        }
    }
  }

  async getItems() {
    if (!this.data.items || isEmpty(this.data.items)) {
      return [];
    }

    // `item` (the projectID+saleID key) and `items` (the id->quantity map) are
    // separate fields, and CALENDAR_EVENT_FIELDS defaults `item` to null — so
    // an event can legitimately carry items with no item. Both branches below
    // slice `item` to derive the project and sale, which throws
    // "Cannot read properties of null (reading 'slice')" in that state. The
    // `items` getter calls this fire-and-forget (no await, no catch), so the
    // rejection escapes as an UNHANDLED one: it never fails the render it came
    // from, it just surfaces later as a process-level error.
    if (!this.data.item) {
      return [];
    }

    // Dedupe concurrent fetches for the same sale: the `items` getter that
    // calls this runs on every render (and the fetch itself commits store
    // changes that trigger re-renders), so without this guard a single expand
    // spawns a storm of overlapping requests until every item has loaded.
    const key = this.data.item;
    if (itemsFetchInFlight.has(key)) {
      return;
    }
    itemsFetchInFlight.add(key);
    try {
      switch (this.type.itemsStore) {
        case "saleItems":
          return await store.dispatch("GET_SALE_ITEMS", {
            projectID: this.data.item.slice(0, -1),
            saleID: this.data.item.slice(-1),
            itemIDs: Object.keys(this.data.items),
          });
        case "saleLabors":
          return await store.dispatch("GET_SALE_LABORS", {
            projectID: this.data.item.slice(0, -1),
            saleID: this.data.item.slice(-1),
            laborIDs: this.data.items,
          });
      }
    } finally {
      itemsFetchInFlight.delete(key);
    }
  }

  async listenItem(options) {
    if (!this.data.item) {
      return null;
    }

    let item = this.data.item;
    switch (this.type.itemStore) {
      case "sales":
        return store.dispatch("LISTEN_SALE", {
          projectID: item.slice(0, -1),
          saleID: item.slice(-1),
          ...options,
        });
    }
  }

  async listenItems() {
    if (!this.data.items || isEmpty(this.data.items)) {
      return [];
    }

    const projectID = this.data.item.slice(0, -1);
    const saleID = this.data.item.slice(-1);

    switch (this.type.itemsStore) {
      case "saleItems":
        return [
          await store.dispatch("LISTEN_SALE_ITEMS", {
            projectID,
            saleID,
          }),
        ];
      case "saleLabors":
        return [
          await store.dispatch("LISTEN_SALE_LABORS", {
            projectID,
            saleID,
          }),
          await store.dispatch("LISTEN_SALE_ITEMS", {
            projectID,
            saleID,
            conditions: ["labor", "in", this.data.items],
          }),
        ];
    }
  }

  occursBetween(start, end, resource) {
    let instances = Object.values(this.data.instances || {});
    const resourceIsVehicle = resource && resource in store.state.main.vehicles;
    return (
      this.data.end >= start &&
      this.data.start <= end &&
      ((instances.length === 0 &&
        (!resource ||
          (resourceIsVehicle
            ? this.data.vehicles.includes(resource)
            : this.data.everyone ||
              this.data.participants.includes(resource)))) ||
        instances.some(
          (i) =>
            i.end >= start &&
            i.start <= end &&
            (!resource ||
              (resourceIsVehicle && i.vehicles
                ? i.vehicles.includes(resource)
                : this.data.vehicles.includes(resource)) ||
              (!resourceIsVehicle && this.data.everyone) ||
              (i.participants
                ? i.participants.includes(resource)
                : this.data.participants.includes(resource)))
        ))
    );
  }

  // A field event that ended before `now`, was the user's, and was never
  // finished on-site: the missed-events widget lists these so the crew can go
  // back and complete them. An event whose completion is still loading (see
  // completedOnSite) isn't reported, so nothing flashes up as missed.
  isMissedBy(userID, now = new Date()) {
    const type = EVENT_TYPES[this.data.type];
    if (!type || !type.isFieldEvent) {
      return false;
    }
    if (this.isPencil || this.data.rescheduleReason) {
      return false;
    }
    if (!this.data.end || this.data.end >= now) {
      return false;
    }
    if (
      !this.data.everyone &&
      (!userID || !this.data.participants.includes(userID))
    ) {
      return false;
    }
    return this.completedOnSite === false;
  }

  // Whether this field event was finished on-site: true or false, or null
  // while what decides it hasn't loaded yet.
  //  - Delivery: completing the stop stamps `status.unloaded` and a delivered
  //    status type. (`status.delivered` can't decide it: it's a per-item map
  //    that sanitize defaults to `{}`, which is always truthy.)
  //  - Service: as a delivery for a parts stop; otherwise its work order is
  //    completed or cancelled. COMPLETE_SERVICE_WORK_ORDER writes only the
  //    work order, never the event.
  //  - Installation: its status has no completion field. A crew finishes a
  //    day by clocking out of the sale's labor, which logs a "clock-out"; the
  //    log doesn't say which event it was for, so a clock-out during the
  //    event's day(s), or a labor completed outright, finishes it.
  get completedOnSite() {
    const status = this.data.status || {};
    const stopCompleted = Boolean(
      status.unloaded || ["delivered", "deliveredPartial"].includes(status.type)
    );
    switch (this.data.type) {
      case "delivery":
        return stopCompleted;
      case "service": {
        if (stopCompleted) {
          return true;
        }
        const workOrder = this.serviceWorkOrder;
        return workOrder
          ? ["completed", "cancelled"].includes(workOrder.data.status)
          : null;
      }
      case "installation": {
        const labors = this.items;
        if (!labors) {
          return null;
        }
        const from = startOfDay(this.data.start);
        const to = endOfDay(this.data.end);
        const finished = labors.some(
          (labor) =>
            labor.data.status.fullyCompleted ||
            Object.values(labor.data.status.log).some(
              (entry) =>
                entry.event === "clock-out" &&
                entry.timestamp >= from &&
                entry.timestamp <= to
            )
        );
        if (finished) {
          return true;
        }
        // A labor still loading may hold the clock-out
        const laborCount = isArray(this.data.items)
          ? this.data.items.length
          : Object.keys(this.data.items).length;
        return labors.length < laborCount ? null : false;
      }
      default:
        return null;
    }
  }

  // A service event's work order (its `item`), loaded on first read
  get serviceWorkOrder() {
    if (this.data.type !== "service" || !this.data.item) {
      return null;
    }
    const workOrder = store.state.main.serviceWorkOrders[this.data.item];
    if (!workOrder) {
      // Fire-and-forget, deduped like `item`: this getter runs on every render
      this.getServiceWorkOrder().catch(() => {});
    }
    return workOrder || null;
  }

  async getServiceWorkOrder() {
    const workOrderID = this.data.item;
    if (workOrderFetchInFlight.has(workOrderID)) {
      return;
    }
    workOrderFetchInFlight.add(workOrderID);
    try {
      return await store.dispatch("GET_SERVICE_WORK_ORDER", { workOrderID });
    } finally {
      workOrderFetchInFlight.delete(workOrderID);
    }
  }

  // A delivery, installation or pickup for a cash sale that still owes what
  // is collected at that stage, for the "cash sale events tomorrow" widget so
  // accounting can arrange payment ahead of time. Material is collected
  // before it ships or is picked up, labor after the install — the same split
  // as the day-before delivery reminder (remindCashSaleDeliveries in
  // functions/calendar.js), whose exclusions this shares: a temporary
  // delivery that isn't invoiced, a canceled sale, and a combined delivery's
  // passengers carrying nothing. Reading `item` / `additionalSalesList` loads
  // the sales when they aren't in the store yet.
  get isCashSaleEvent() {
    if (this.isPencil || this.data.rescheduleReason) {
      return false;
    }
    if (!["delivery", "installation", "pickup"].includes(this.data.type)) {
      return false;
    }
    const status = this.data.status || {};
    if (status.isTemporary && !status.shouldInvoiceAnyway) {
      return false;
    }
    const amountDue = (sale) =>
      this.data.type === "installation"
        ? sale.openBalance
        : sale.materialAmountDue;
    const owes = (sale) =>
      Boolean(
        sale &&
          sale.data.terms.cashSale &&
          !sale.data.canceled &&
          amountDue(sale) > 0
      );
    if (owes(this.item)) {
      return true;
    }
    // A combined delivery's passengers need collecting as much as its own sale
    return this.additionalSalesList.some(
      (passenger) => passenger.quantity > 0 && owes(passenger.sale)
    );
  }

  getInstances(start, end, resource) {
    const cacheKey = `${start.getTime()}|${end.getTime()}|${resource || ""}`;
    let cache = instancesCache.get(this);
    if (!cache) {
      cache = {};
      instancesCache.set(this, cache);
    }
    if (cache[cacheKey]) {
      return cache[cacheKey];
    }

    let instanceIDs = Object.keys(this.data.instances || {});
    const resourceIsVehicle = resource && resource in store.state.main.vehicles;
    let result;
    if (instanceIDs.length === 0) {
      result =
        this.data.end >= start &&
        this.data.start <= end &&
        (!resource ||
          (resourceIsVehicle
            ? this.data.vehicles.includes(resource)
            : this.data.everyone || this.data.participants.includes(resource)))
          ? [this]
          : [];
    } else if (instanceIDs.length === 1) {
      // Single instance - just return this event as the instance
      const id = instanceIDs[0];
      const i = this.data.instances[id];
      if (
        i.end >= start &&
        i.start <= end &&
        (!resource ||
          (resourceIsVehicle && i.vehicles
            ? i.vehicles.includes(resource)
            : this.data.vehicles.includes(resource)) ||
          (!resourceIsVehicle && this.data.everyone) ||
          (i.participants
            ? i.participants.includes(resource)
            : this.data.participants.includes(resource)))
      ) {
        result = [new CalendarEvent(this.id, this.data, id)];
      } else {
        result = [];
      }
    } else {
      instanceIDs = instanceIDs.filter((id) => {
        let i = this.data.instances[id];
        return (
          i.end >= start &&
          i.start <= end &&
          (!resource ||
            (resourceIsVehicle && i.vehicles
              ? i.vehicles.includes(resource)
              : this.data.vehicles.includes(resource)) ||
            (!resourceIsVehicle && this.data.everyone) ||
            (i.participants
              ? i.participants.includes(resource)
              : this.data.participants.includes(resource)))
        );
      });
      result = instanceIDs
        .map((id) => new CalendarEvent(this.id, this.data, id))
        .sort(eventSort);
    }

    cache[cacheKey] = result;
    return result;
  }

  getInstancesAsEvent(start, end, resource) {
    const cacheKey = `${start.getTime()}|${end.getTime()}|${resource || ""}`;
    let cache = instancesAsEventCache.get(this);
    if (!cache) {
      cache = {};
      instancesAsEventCache.set(this, cache);
    }
    if (cacheKey in cache) {
      return cache[cacheKey];
    }

    let instanceIDs = Object.keys(this.data.instances || {});
    const resourceIsVehicle = resource && resource in store.state.main.vehicles;
    let result;
    if (instanceIDs.length === 0) {
      result =
        this.data.end >= start &&
        this.data.start <= end &&
        (!resource ||
          (resourceIsVehicle
            ? this.data.vehicles.includes(resource)
            : this.data.everyone || this.data.participants.includes(resource)))
          ? this
          : null;
    } else {
      instanceIDs = instanceIDs.filter((id) => {
        let i = this.data.instances[id];
        return (
          i.end >= start &&
          i.start <= end &&
          (!resource ||
            (resourceIsVehicle && i.vehicles
              ? i.vehicles.includes(resource)
              : this.data.vehicles.includes(resource)) ||
            (!resourceIsVehicle && this.data.everyone) ||
            (i.participants
              ? i.participants.includes(resource)
              : this.data.participants.includes(resource)))
        );
      });
      if (!instanceIDs.length) {
        result = null;
      } else {
        const instances = instanceIDs.map((id) => this.data.instances[id]);
        const data = {
          ...this.data,
          instances: pick(this.data.instances, instanceIDs),
        };
        data.start = Math.min(...instances.map((i) => i.start));
        data.end = Math.max(...instances.map((i) => i.end));
        data.participants = uniq(
          compact(
            flatten(
              instances.map((i) => i.participants || this.data.participants)
            )
          )
        );
        data.vehicles = uniq(
          compact(
            flatten(instances.map((i) => i.vehicles || this.data.vehicles))
          )
        );
        result = new CalendarEvent(this.id, data);
      }
    }

    cache[cacheKey] = result;
    return result;
  }

  get iid() {
    return this.id
      ? this.id + (this.instanceID ? "-" + this.instanceID : "")
      : null;
  }

  // Editing

  get isPencil() {
    return Boolean(this.data.pencil);
  }

  get rules() {
    const required = (a) => Boolean(a && a.trim());
    if (this.isPencil) {
      return {
        type: [
          (t) => required(t) || "required",
          (t) => t in EVENT_TYPES || "eventTypeInvalid",
        ],
        item: [
          (i) =>
            Boolean(!this.data.type || !this.type.itemStore || i) || "required",
        ],
        title: [required],
        start: [() => required(this.startString) || "required"],
        end: [
          () => (this.data.end && this.end > this.start) || "endAfterStart",
        ],
        participants: [(p) => p.length > 0 || this.data.everyone || "required"],
      };
    }
    return {
      type: [
        (a) => Boolean(a && a.trim()) || "required",
        (t) => t in EVENT_TYPES || "eventTypeInvalid",
      ],
      item: [
        (i) =>
          Boolean(!this.data.type || !this.type.itemStore || i) || "required",
      ],
      items: [
        (i) =>
          !this.type.itemsStore ||
          (i && (isArray(i) ? i : Object.keys(i)).length > 0) ||
          "required",
      ],
      title: [(a) => Boolean(a && a.trim()) || "required"],
      start: [() => Boolean(this.startString?.trim()) || "required"],
      end: [() => (this.data.end && this.end > this.start) || "endAfterStart"],
      location: [
        (l) =>
          this.data.type === "pickup"
            ? Boolean(l) || "required"
            : !this.data.location ||
              this.data.type === "ooo" ||
              this.data.type === "personal" ||
              isString(this.data.location) ||
              this.location.isValid ||
              "validLocationRequired",
      ],
      participants: [
        (p) =>
          p.length > 0 ||
          this.data.everyone ||
          this.data.type === "pickup" ||
          "required",
      ],
      vehicles: [
        (v) => v.length > 0 || !this.type.requiresVehicle || "required",
      ],
    };
  }

  isEndDateValid(end) {
    let start = this.startString.slice(0, 10);
    return end >= start;
  }

  get isValid() {
    const data = CalendarEvent.sanitize(cloneDeep(this.data));
    return every(this.rules, (rules, key) =>
      rules.every((rule) => rule(data[key]) === true)
    );
  }

  get isEmpty() {
    return Boolean(!this.id && isEmpty(diff(this.data, EVENT_FIELDS)));
  }

  isEqual(event) {
    // Use removeUnsaved=false so valid-but-unsaved attachments (e.g. a
    // newly selected file with toSave but no url yet) are kept in the
    // diff.  With removeUnsaved=true, cleanDocuments strips them from
    // both sides, masking the change and leaving isSaved true.
    const changes = diff(
      CalendarEvent.sanitize(cloneDeep(this.data), false),
      CalendarEvent.sanitize(cloneDeep(event.data), false)
    );
    return Boolean(this.id === event.id && isEmpty(changes));
  }

  isRescheduled(event) {
    return (
      event.data.start.getTime() !== this.data.start.getTime() ||
      event.data.end.getTime() !== this.data.end.getTime() ||
      !isEmpty(diff(event.data.instances, this.data.instances))
    );
  }

  static cleanDocuments(data, removeUnsaved = false) {
    data.attachments = data.attachments.filter(
      (d) => new DocumentRef(d)[removeUnsaved ? "isSaved" : "isValid"]
    );
    return data;
  }

  static sanitizeInstances(instances) {
    // Remove invalid instances
    if (instances && Object.keys(instances).length > 0) {
      instances = pickBy(instances, (i) => i.start && i.end);
    }
    return instances || {};
  }

  static sanitize(data, removeUnsaved) {
    data.title = data.title.trim();
    data.description = data.description.trim();

    data.instances = CalendarEvent.sanitizeInstances(data.instances);
    // If only one instance, remove instances
    if (Object.keys(data.instances).length === 1) {
      let instance = data.instances[Object.keys(data.instances)[0]];
      data.start = instance.start;
      data.end = instance.end;
      data.instances = {};
    }
    // Set start and end to encompass all instances
    if (Object.keys(data.instances).length > 0) {
      Object.assign(data, getInstanceRange(data.instances));
    }

    if (data.allDay) {
      data.start = startOfDay(data.start);
      data.end = endOfDay(data.end);
    }
    if (data.everyone) {
      data.participants = [];
    }
    // Installation event items are an array of labor IDs. The selector can
    // reorder them by line number, so normalize order before comparing or
    // saving so an untouched event isn't flagged as changed.
    if (data.type === "installation" && isArray(data.items)) {
      const saleLabors = store.state.main.saleLabors;
      data.items = sortBy([...data.items], (id) => {
        const labor = saleLabors?.[id];
        return labor?.line ?? Number.POSITIVE_INFINITY;
      });
    }
    if (
      data.location &&
      !new Address(data.location).isValid &&
      data.location.street
    ) {
      data.location = data.location.street;
    }
    if (
      data.location &&
      data.location.type === "jobsite" &&
      data.status.isTemporary
    ) {
      data.status.isTemporary = false;
    }
    CalendarEvent.cleanDocuments(data, removeUnsaved);
    return data;
  }

  get instances() {
    return Object.keys(this.data.instances || {})
      .map((id) => new CalendarEvent(this.id, this.data, id))
      .sort(eventSort);
  }

  get nextInstance() {
    const now = new Date();
    const instances = this.instances;
    let hasHappened = false;
    let start = this.start;
    let end = this.end;
    if (instances.length) {
      for (const instance of instances) {
        start = instance.start;
        end = instance.end;
        if (start <= now && end > now) {
          // Present
          return instance;
        } else if (end < now) {
          // Past
          hasHappened = true;
        } else if (hasHappened) {
          // Future
          return instance;
        }
      }
    } else {
      if (start <= now && end > now) {
        // Present
        return this;
      } else if (end <= now) {
        // Past
        hasHappened = true;
      } else if (hasHappened) {
        // Future
        return this;
      }
    }
    return null;
  }

  get currentVehicles() {
    const event = this.nextEventInstance;
    if (!event) {
      return [];
    }
    return event.vehicles || this.vehicles;
  }

  get currentParticipants() {
    const event = this.nextEventInstance;
    if (!event) {
      return [];
    }
    return event.participants || this.participants;
  }

  get groupedInstances() {
    const cached = groupedInstancesCache.get(this);
    if (cached) {
      return cached;
    }
    let instances = Object.values(this.data.instances || {});
    if (instances.length === 0 && this.start && this.end) {
      // Single-instance events store no instances; surface the event itself
      // as one group so it still renders in lists like SaleLabor's man-hours.
      instances = [
        {
          start: this.start,
          end: this.end,
          participants: this.data.participants,
          vehicles: this.data.vehicles,
        },
      ];
    }
    const groups = groupBy(sortBy(instances, ["start", "end"]), (instance) =>
      format(instance.start, i18n.global.t("format.date-event"))
    );
    const groupValues = Object.values(groups);
    groupValues.map((group) => {
      group.participants = uniq(
        compact(
          flatten(group.map((i) => i.participants || this.data.participants))
        )
      );
      group.vehicles = uniq(
        compact(flatten(group.map((i) => i.vehicles || this.data.vehicles)))
      );
    });
    if (
      groupValues.every((group) =>
        isEqual(group.participants, this.data.participants)
      )
    ) {
      groupValues.forEach((group) => delete group.participants);
    }
    if (
      groupValues.every((group) => isEqual(group.vehicles, this.data.vehicles))
    ) {
      groupValues.forEach((group) => delete group.vehicles);
    }
    groupedInstancesCache.set(this, groups);
    return groups;
  }

  get hasInstances() {
    return Object.keys(this.data.instances || {}).length > 1;
  }

  get isInstance() {
    return Boolean(this.instanceID);
  }

  // Formatting

  get itemRoute() {
    if (!this.type) {
      return null;
    }

    switch (this.type.itemStore) {
      case "sales":
        return {
          routeType: "page",
          name: "sale-view",
          params: {
            projectID: this.data.item.slice(0, -1),
            saleID: this.data.item.slice(-1),
          },
        };
      // case "saleLabors":
      //   return {
      //     routeType: "page",
      //     name: "labor-view",
      //     params: {
      //       projectID: this.data.item.slice(0, -1),
      //       saleID: this.data.item.slice(-1),
      //     },
      //   };
      default:
        return null;
    }
  }

  get defaultTitle() {
    if (!this.item) {
      return "";
    }

    switch (this.data.type) {
      case "delivery":
        if (this.item.project) {
          // Combined deliveries ship sibling sales too — list every sale's
          // letter after the project part of the PO, sorted alphabetically
          // regardless of which sale is primary.
          const letters = uniq([
            this.item.id,
            ...Object.keys(this.data.additionalSales || {}).map((stateID) =>
              stateID.slice(-1)
            ),
          ]).sort();
          return `${this.item.projectID}${letters.join(
            ","
          )} – ${this.item.project.toString()}`;
        }
        return "";
      case "installation":
      case "service":
        if (this.item.project) {
          return `${this.item.toString()} – ${this.item.project.toString()}`;
        }
        return "";
      case "pickup":
        return `CPU-${this.data.item}`;
      default:
        return "";
    }
  }

  get defaultLocation() {
    if (!this.item) {
      return null;
    }

    switch (this.data.type) {
      case "delivery":
        return { ...this.item.shipTo.data };
      case "pickup":
        return null;
      case "installation":
        if (this.item.project) {
          return {
            type: "jobsite",
            address: { ...this.item.project.address.data },
          };
        }
    }
  }

  get start() {
    if (!this.data.start) {
      return null;
    }
    if (this.data.allDay) {
      return startOfDay(this.data.start);
    }
    return this.data.start;
  }

  get startString() {
    if (!this.data.start) {
      return "";
    }
    return dateToTimestamp(this.data.start, this.data.allDay);
  }

  get startDateString() {
    return this.startString.slice(0, 10).replace(/\//g, "-");
  }

  get end() {
    if (!this.data.end) {
      return null;
    }
    if (this.data.allDay) {
      return endOfDay(this.data.end);
    }
    return this.data.end;
  }

  get endString() {
    if (!this.data.end) {
      return "";
    }
    return dateToTimestamp(this.data.end, this.data.allDay);
  }

  get endDateString() {
    return this.endString.slice(0, 10).replace(/\//g, "-");
  }

  get dateTimeHTML() {
    let html = "";
    if (this.isMultiDay) {
      let template = i18n.global.t(
        this.allDay ? "format.date-short" : "format.date-time-short"
      );
      html += `<div>${format(this.start, template)}</div>`;
      html += `<div>${i18n.global.t("to")}</div>`;
      html += `<div>${format(this.end, template)}</div>`;
    } else if (this.allDay) {
      html += format(this.start, i18n.global.t("format.date-short"));
    } else {
      let template = i18n.global.t("format.time-short");
      html += `<div>${format(
        this.start,
        i18n.global.t("format.date-short")
      )} –</div>`;
      html += `<div>${format(this.start, template)}`;
      html += ` ${i18n.global.t("to")} `;
      html += `${format(this.end, template)}</div>`;
    }
    return `<div class="row q-gutter-xs">${html}</div>`;
  }

  get timeHTML() {
    let html = "";
    if (this.isMultiDay) {
      let template = i18n.global.t(
        this.allDay ? "format.date-short" : "format.date-time-short"
      );
      html += `<div>${format(this.start, template)}</div>`;
      html += `<div>${i18n.global.t("to")}</div>`;
      html += `<div>${format(this.end, template)}</div>`;
    } else if (this.allDay) {
      html += format(this.start, i18n.global.t("format.date-short"));
    } else {
      let template = i18n.global.t("format.time-short");
      html += `<div>${format(this.start, template)}`;
      html += ` ${i18n.global.t("to")} `;
      html += `${format(this.end, template)}</div>`;
    }
    return `<div class="row q-gutter-xs">${html}</div>`;
  }

  static instanceGroupTimesHTML(instanceGroup, includeDate) {
    let times = [];
    let prevEnd = null;
    instanceGroup.forEach((instance) => {
      if (!prevEnd || !isEqual(instance.start, prevEnd)) {
        times.push({ ...instance });
        prevEnd = instance.end;
      } else {
        last(times).end = instance.end;
      }
    });
    const html =
      (includeDate
        ? "<div>" +
          format(instanceGroup[0].start, i18n.global.t("format.date-short")) +
          " –</div>"
        : "") +
      "<div>" +
      times
        .map((instance) =>
          instance.start && instance.end
            ? format(instance.start, i18n.global.t("format.time-short")) +
              ` ${i18n.global.t("to")} ` +
              format(instance.end, i18n.global.t("format.time-short"))
            : ""
        )
        .join(",</div><div>") +
      "</div>";
    return `<div class="row q-gutter-xs">${html}</div>`;
  }

  get duration() {
    if (!this.data.start || !this.data.end) {
      return "";
    }
    if (this.hasInstances) {
      return this.instances.reduce((total, i) => total + (i.duration || 0), 0);
    }
    let start = this.start.getTime();
    let end = this.end.getTime();
    if (this.data.allDay) {
      end += 1;
    }
    return start && end && start < end ? end - start : null;
  }

  set duration(duration) {
    if (this.data.start && !this.hasInstances) {
      this.data.end = new Date(
        this.start.getTime() + duration - 1 * this.data.allDay
      );
    }
  }

  get hours() {
    return this.duration / 36e5;
  }

  get manHours() {
    return (
      this.hasInstances ? Object.values(this.data.instances) : [this.data]
    ).reduce(
      (total, instance) =>
        total +
        ((instance.participants
          ? instance.participants.length
          : this.data.participants.length) *
          (instance.end - instance.start)) /
          36e5,
      0
    );
  }

  get durationFormatted() {
    return formatDurationMs(this.duration);
  }

  get startTime() {
    return this.data.allDay ? null : this.startString.slice(-5);
  }

  get endTime() {
    return this.data.allDay ? null : this.endString.slice(-5);
  }

  get durationMinutes() {
    return this.duration / 6e4;
  }

  hasStarted(date) {
    let start = this.start;
    if (date) {
      let groupedInstances = this.groupedInstances;
      if (date in groupedInstances) {
        start = groupedInstances[date][0].start;
      }
    }
    return start <= new Date();
  }

  hasEnded(date) {
    let end = this.end;
    if (date) {
      let groupedInstances = this.groupedInstances;
      if (date in groupedInstances) {
        end = last(groupedInstances[date]).end;
      }
    }
    return end < new Date();
  }

  isOngoing(dateString) {
    const now = new Date();
    let start = this.start;
    let end = this.end;
    if (dateString) {
      let groupedInstances = this.groupedInstances;
      if (dateString in groupedInstances) {
        start = groupedInstances[dateString][0].start;
        end = last(groupedInstances[dateString]).end;
      }
    }
    return start <= now && end > now;
  }

  relativeDate(date) {
    if (date) {
      let groupedInstances = this.groupedInstances;
      if (date in groupedInstances) {
        let start = groupedInstances[date][0].start;
        return start <= new Date() ? last(groupedInstances[date]).end : start;
      }
    }
    return this.hasStarted() ? this.end : this.start;
  }

  relativeVerb(date) {
    return this.hasStarted(date)
      ? this.hasEnded(date)
        ? "Ended"
        : "Ends"
      : "Starts";
  }

  get nowLabel() {
    if (this.isMultiDay) {
      return "Ongoing";
    } else {
      return this.allDay ? "Today" : "Now";
    }
  }

  get isMultiDay() {
    return !isSameDay(this.start, this.end);
  }

  isStartDate(date) {
    return date === this.startDateString;
  }

  isEndDate(date) {
    return date === this.endDateString;
  }

  get type() {
    const type = EVENT_TYPES[this.data.type];
    return type ? { ...type, ...COLORS[type.color] } : null;
  }

  get location() {
    if (isObject(this.data.location)) {
      if ("address" in this.data.location) {
        return new ShipTo(this.data.location);
      } else {
        return new Address(this.data.location);
      }
    }
    return this.data.location;
  }

  get stops() {
    if (!this.data.stops || !this.data.stops.length) {
      return [];
    }
    return this.data.stops.map((stop) => new Address(stop));
  }

  // Location value(s) used for location-based filtering. Uses the linked
  // sale's location when present, otherwise derives from the participant(s).
  get locationValues() {
    if (this.data.saleLocation) {
      return [this.data.saleLocation];
    }
    const locations = [];
    (this.data.participants || []).forEach((id) => {
      const user = store.state.main.users[id];
      if (user && Array.isArray(user.data.locations)) {
        locations.push(...user.data.locations);
      }
    });
    return uniq(compact(locations));
  }

  get saleLocation() {
    return this.data.saleLocation
      ? LOCATION_INFO[this.data.saleLocation]
      : null;
  }

  get attachments() {
    return this.data.attachments.map((d) => new DocumentRef(d));
  }

  get participants() {
    return compact(
      this.data.participants.map((id) => store.state.main.contacts[id])
    );
  }

  get item() {
    if (!this.data.item || !this.data.type || !this.type?.itemStore) {
      return null;
    }
    let item = store.state.main[this.type.itemStore]?.[this.data.item];
    if (!item) {
      // Fire-and-forget lazy load; this getter runs on every render, so a
      // dangling item reference would otherwise reject once per render.
      this.getItem().catch(() => {});
    }
    return item;
  }

  get items() {
    if (!this.data.items || isEmpty(this.data.items)) {
      return null;
    }
    const itemIDs = isArray(this.data.items)
      ? this.data.items
      : Object.keys(this.data.items);
    let items;
    const itemsStore = store.state.main[this.type.itemsStore];
    if (this.type.itemsStore === "saleItems") {
      const projectID = this.data.item.slice(0, -1);
      const saleID = this.data.item.slice(-1);
      items = compact(
        itemIDs.map((id) =>
          id in itemsStore
            ? deepFreeze(
                new SaleItem(projectID, saleID, id, {
                  ...itemsStore[id].data,
                  quantity: this.data.items[id],
                })
              )
            : null
        )
      ).sort((a, b) => a.line - b.line || a.accessoryIndex - b.accessoryIndex);
    } else {
      items = compact(itemIDs.map((id) => itemsStore[id])).sort(
        (a, b) => a.line - b.line
      );
    }
    if (itemIDs.length && items.length < itemIDs.length) {
      this.getItems();
    }
    return items;
  }

  get additionalSalesList() {
    if (
      !this.data.additionalSales ||
      isEmpty(this.data.additionalSales) ||
      this.data.type !== "delivery"
    ) {
      return [];
    }
    const salesStore = store.state.main.sales;
    const itemsStore = store.state.main.saleItems;
    const result = [];
    let needsFetch = false;

    for (const [saleStateID, saleData] of Object.entries(
      this.data.additionalSales
    )) {
      const sale = salesStore[saleStateID];
      if (!sale) {
        needsFetch = true;
        continue;
      }
      const projectID = saleStateID.slice(0, -1);
      const saleID = saleStateID.slice(-1);
      const itemIDs = Object.keys(saleData.items || {});
      const items = compact(
        itemIDs.map((id) =>
          id in itemsStore
            ? deepFreeze(
                new SaleItem(projectID, saleID, id, {
                  ...itemsStore[id].data,
                  quantity: saleData.items[id],
                })
              )
            : null
        )
      ).sort((a, b) => a.line - b.line || a.accessoryIndex - b.accessoryIndex);

      if (itemIDs.length && items.length < itemIDs.length) {
        needsFetch = true;
      }

      result.push({ sale, items, quantity: saleData.quantity || 0 });
    }

    if (needsFetch) {
      this.getAdditionalSales();
    }

    return result;
  }

  async getAdditionalSales() {
    if (!this.data.additionalSales || isEmpty(this.data.additionalSales)) {
      return;
    }
    for (const saleStateID of Object.keys(this.data.additionalSales)) {
      // Dedupe per sale, exactly as `getItems` does: `additionalSalesList` is
      // a getter that runs on every render and the fetch commits store changes
      // that trigger the next render, so without this guard one combined
      // delivery spawns an unbounded fetch/render storm.
      if (additionalSalesFetchInFlight.has(saleStateID)) {
        continue;
      }
      additionalSalesFetchInFlight.add(saleStateID);
      const projectID = saleStateID.slice(0, -1);
      const saleID = saleStateID.slice(-1);
      const saleData = this.data.additionalSales[saleStateID];
      try {
        await store.dispatch("GET_SALE", { projectID, saleID });
        await store.dispatch("GET_SALE_ITEMS", {
          projectID,
          saleID,
          itemIDs: Object.keys(saleData.items || {}),
        });
      } finally {
        additionalSalesFetchInFlight.delete(saleStateID);
      }
    }
  }

  get itemCount() {
    return this.type.itemsStore
      ? Object.values(this.data.items).reduce(
          (total, count) => total + count,
          0
        )
      : null;
  }

  get unitCount() {
    if (!this.type.itemsStore) return null;
    // `this.items` returns null when data.items is empty (see items
    // getter above). updateSale's pre/post-save reads (events.js)
    // call into these counts and used to crash with "Cannot read
    // properties of null (reading 'reduce')" on a pencil save where
    // items hadn't been populated yet. Treat no-items as zero count;
    // null is still reserved for "not an item-bearing event type."
    //
    // Child accessories are excluded here and counted by `accessoryCount`
    // instead, so scheduling a delivery that carries one no longer inflates
    // `status.delivery.scheduled` past the quote-line based `total.items`.
    // Mirrors `classifyItemBucket` (`src/store/main/actions/sales.js`).
    return (this.items || []).reduce(
      (total, i) =>
        total +
        (i.data.parentID || ((i.isScreens || i.isHardware) && !i.data.line)
          ? 0
          : i.quantity),
      0
    );
  }

  get accessoryCount() {
    if (!this.type.itemsStore) return null;
    return (this.items || []).reduce(
      (total, i) => total + (i.data.parentID ? i.quantity : 0),
      0
    );
  }

  get screenCount() {
    if (!this.type.itemsStore) return null;
    return (this.items || []).reduce(
      (total, i) =>
        total +
        (!i.data.parentID && i.isScreens && !i.data.line ? i.quantity : 0),
      0
    );
  }

  get hardwareCount() {
    if (!this.type.itemsStore) return null;
    return (this.items || []).reduce(
      (total, i) =>
        total +
        (!i.data.parentID && i.isHardware && !i.data.line ? i.quantity : 0),
      0
    );
  }

  get vehicles() {
    return compact(
      this.data.vehicles.map((id) => store.state.main.vehicles[id])
    );
  }

  get createdBy() {
    return this.data.createdBy && store.state?.main?.users
      ? store.state.main.users[this.data.createdBy]
      : null;
  }

  get modifiedBy() {
    return this.data.modifiedBy && store.state?.main?.users
      ? store.state.main.users[this.data.modifiedBy]
      : null;
  }

  get status() {
    // Pencil events are tentative — they should not show any status progression
    if (this.isPencil) {
      return null;
    }

    // Check cache first to avoid expensive Status object creation with cloneDeep
    const cached = statusCache.get(this);
    if (cached !== undefined) {
      return cached;
    }

    let result;
    switch (this.data.type) {
      case "delivery":
        result = new DeliveryStatus(this, this.data.status);
        break;
      case "pickup":
        result = new PickupStatus(this, this.data.status);
        break;
      case "installation":
        if (this.items && this.items.length) {
          result = last(
            sortBy(
              this.items.map((item) => item.status),
              "type.order"
            ).reverse()
          );
        } else if (this.data.rescheduleReason) {
          // Fallback: show reschedule status from event when items aren't loaded.
          // Consumers read `status.data.type` as well as `status.type` (the
          // SaleLabor page's phaseButtons, the SaleLabor card's transit block),
          // so a `type`-only object throws "Cannot read properties of undefined
          // (reading 'type')" mid-render. Carry `data` too.
          const rescheduleType = SALE_LABOR_STATUS_TYPES.reschedule;
          // Returned WITHOUT caching: this is a placeholder for labors that
          // have not loaded yet, and the cache is keyed on the event object,
          // which outlives that window — caching it would keep serving the
          // placeholder long after the real labor statuses were available.
          return {
            data: { type: "reschedule" },
            type: { ...rescheduleType, ...COLORS[rescheduleType.color] },
          };
        } else {
          result = null;
        }
        break;
      case "service":
        result = new ServiceStatus(this, this.data.status);
        break;
      default:
        result = null;
    }

    statusCache.set(this, result);
    return result;
  }

  get description() {
    if (!this.data.description) {
      return "";
    }

    // Escape HTML to prevent XSS
    let html = this.data.description
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;");

    // Detect and convert phone numbers to links
    // Matches various phone number formats:
    // - (123) 456-7890
    // - 123-456-7890
    // - 123.456.7890
    // - 1234567890
    // - +1 (123) 456-7890
    // - With optional extensions
    const phoneRegex =
      /(\+?\d{1,3}[\s.-]?)?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}(\s*(ext|x|extension)[\s.:]*\d+)?/gi;

    html = html.replace(phoneRegex, (match) => {
      // Extract just the digits and extension
      const digits = match.replace(/\D/g, "");
      const extMatch = match.match(/(ext|x|extension)[\s.:]*(\d+)/i);
      const ext = extMatch ? extMatch[2] : "";

      // Determine if it's a valid phone number (10 digits for US, or 11 with country code)
      let phoneNumber = digits;
      if (digits.length === 11 && digits[0] === "1") {
        phoneNumber = digits.slice(1);
      } else if (digits.length !== 10) {
        // Not a valid phone number format, return as-is
        return match;
      }

      // Create tel link
      const telLink = "tel:+1" + phoneNumber + (ext ? ";" + ext : "");

      return `<a href="${telLink}">${match}</a>`;
    });

    // Preserve line breaks
    html = html.replace(/\n/g, "<br>");

    return html;
  }

  toString() {
    return this.data.title;
  }
}
